import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import api from './services/api';
import SetupPage from './pages/SetupPage';
import LoginPage from './pages/LoginPage';
import CustomerLayout from './layouts/CustomerLayout';
import StaffLayout from './layouts/StaffLayout';
import CustomerDashboard from './pages/customer/CustomerDashboard';
import CustomerNewTicket from './pages/customer/CustomerNewTicket';
import CustomerTicketDetail from './pages/customer/CustomerTicketDetail';
import PublicTicketView from './pages/public/PublicTicketView';
import PublicNewRequest from './pages/public/PublicNewRequest';
import CustomerSupportPage from './pages/public/CustomerSupportPage';
import StaffDashboard from './pages/staff/StaffDashboard';
import StaffTicketBoard from './pages/staff/StaffTicketBoard';
import StaffTicketDetail from './pages/staff/StaffTicketDetail';
import CustomersPage from './pages/staff/CustomersPage';
import UsersPage from './pages/staff/UsersPage';
import SettingsPage from './pages/staff/SettingsPage';
import CalendarPage from './pages/staff/CalendarPage';
import ReportsPage from './pages/staff/ReportsPage';
import CannedResponsesPage from './pages/staff/CannedResponsesPage';
import KnowledgeBasePage from './pages/staff/KnowledgeBasePage';
import SLAPage from './pages/staff/SLAPage';
import AutoAssignPage from './pages/staff/AutoAssignPage';
import ActivityLogPage from './pages/staff/ActivityLogPage';
import SatisfactionPage from './pages/staff/SatisfactionPage';
import LogoTicketsPage from './pages/staff/LogoTicketsPage';

function PrivateRoute({ children, staffOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  if (!user) return <Navigate to="/login" />;
  if (staffOnly && user.role === 'customer') return <Navigate to="/dashboard" />;
  return children;
}

function AppRoutes({ dbConfigured }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  // DB yapılandırılmamışsa her yerde setup'a yönlendir
  if (!dbConfigured) {
    return (
      <Routes>
        <Route path="/setup" element={<SetupPage />} />
        <Route path="*" element={<Navigate to="/setup" />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/setup" element={<SetupPage />} />
      <Route path="/" element={user ? <Navigate to={user.role === 'customer' ? '/dashboard' : '/staff/dashboard'} /> : <LoginPage />} />
      <Route path="/login" element={user ? <Navigate to={user.role === 'customer' ? '/dashboard' : '/staff/dashboard'} /> : <LoginPage />} />

      {/* Public Routes - Şifresiz erişim */}
      <Route path="/support/:token" element={<CustomerSupportPage />} />
      {/* /request devre dışı - talepler müşteri destek linki üzerinden gelir */}
      {/* <Route path="/request" element={<PublicNewRequest />} /> */}
      <Route path="/track/:token" element={<PublicTicketView />} />

      {/* Customer Routes (şifreli giriş ile detaylı geçmiş) */}
      <Route path="/dashboard" element={<PrivateRoute><CustomerLayout><CustomerDashboard /></CustomerLayout></PrivateRoute>} />
      <Route path="/new-ticket" element={<PrivateRoute><CustomerLayout><CustomerNewTicket /></CustomerLayout></PrivateRoute>} />
      <Route path="/tickets/:id" element={<PrivateRoute><CustomerLayout><CustomerTicketDetail /></CustomerLayout></PrivateRoute>} />

      {/* Staff Routes */}
      <Route path="/staff/dashboard" element={<PrivateRoute staffOnly><StaffLayout><StaffDashboard /></StaffLayout></PrivateRoute>} />
      <Route path="/staff/tickets" element={<PrivateRoute staffOnly><StaffLayout><StaffTicketBoard /></StaffLayout></PrivateRoute>} />
      <Route path="/staff/tickets/:id" element={<PrivateRoute staffOnly><StaffLayout><StaffTicketDetail /></StaffLayout></PrivateRoute>} />
      <Route path="/staff/logo-tickets" element={<PrivateRoute staffOnly><StaffLayout><LogoTicketsPage /></StaffLayout></PrivateRoute>} />
      <Route path="/staff/customers" element={<PrivateRoute staffOnly><StaffLayout><CustomersPage /></StaffLayout></PrivateRoute>} />
      <Route path="/staff/users" element={<PrivateRoute staffOnly><StaffLayout><UsersPage /></StaffLayout></PrivateRoute>} />
      <Route path="/staff/settings" element={<PrivateRoute staffOnly><StaffLayout><SettingsPage /></StaffLayout></PrivateRoute>} />
      <Route path="/staff/calendar" element={<PrivateRoute staffOnly><StaffLayout><CalendarPage /></StaffLayout></PrivateRoute>} />
      <Route path="/staff/reports" element={<PrivateRoute staffOnly><StaffLayout><ReportsPage /></StaffLayout></PrivateRoute>} />
      <Route path="/staff/canned-responses" element={<PrivateRoute staffOnly><StaffLayout><CannedResponsesPage /></StaffLayout></PrivateRoute>} />
      <Route path="/staff/knowledge-base" element={<PrivateRoute staffOnly><StaffLayout><KnowledgeBasePage /></StaffLayout></PrivateRoute>} />
      <Route path="/staff/sla" element={<PrivateRoute staffOnly><StaffLayout><SLAPage /></StaffLayout></PrivateRoute>} />
      <Route path="/staff/auto-assign" element={<PrivateRoute staffOnly><StaffLayout><AutoAssignPage /></StaffLayout></PrivateRoute>} />
      <Route path="/staff/activity-log" element={<PrivateRoute staffOnly><StaffLayout><ActivityLogPage /></StaffLayout></PrivateRoute>} />
      <Route path="/staff/satisfaction" element={<PrivateRoute staffOnly><StaffLayout><SatisfactionPage /></StaffLayout></PrivateRoute>} />

      <Route path="*" element={<Navigate to={user ? (user.role === 'customer' ? '/dashboard' : '/staff/dashboard') : '/login'} />} />
    </Routes>
  );
}

function App() {
  const [dbConfigured, setDbConfigured] = useState(null);

  useEffect(() => {
    api.get('/settings/check')
      .then(res => setDbConfigured(res.data.configured))
      .catch(() => setDbConfigured(false));
  }, []);

  if (dbConfigured === null) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-center">
          <img src="/logo.png" alt="DSY" className="h-16 mx-auto mb-4" />
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-slate-500 mt-3 text-sm">Sistem kontrol ediliyor...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <AuthProvider>
        <AppRoutes dbConfigured={dbConfigured} />
        <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover theme="colored" />
      </AuthProvider>
    </Router>
  );
}

export default App;
